import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import henceforthApi from '../../utils/henceforthApi';
import { GlobalContext, handleError } from '../../context/Provider';
import BreadCrumb from '../../components/common/BreadCrumb';
import Inputs, { SaveCancelButton } from '../../components/common/Inputs';
import Spinner from '../../components/common/spinner';
import imageUpload from '../../assets/images/pages/dummy-image.jpg';
import authAction from '../../context/authAction';
import SignedImage from '../../components/common/SignedImage';

const BankDetails = () => {
    const { language_data, authState, loading, setLoading, toastMessage, authDispatch } = useContext(GlobalContext);
    const navigate = useNavigate();

    const [state, setState] = useState({
        account_holder_name: '',
        account_number: '',
        routing_number: '',
        bank_name: '',
        business_website: '',
        industry: '',
        ssn_last4_number: '',
        date_of_birth: '',
        document_file_key: '',
        language: 'ENGLISH',
    });

    const [errors, setErrors] = useState<any>({});
    const [selectedFile, setSelectedFile] = useState<any>(null);

    // Industry options
    const industryOptions = [
        { value: '5411', label: 'Grocery Stores - Food & groceries' },
        { value: '5812', label: 'Restaurants - Food service' },
        { value: '5651', label: 'Family Clothing - Apparel retail' },
        { value: '5732', label: 'Electronics Stores - Tech retail' },
        { value: '5999', label: 'Miscellaneous Retail - General retail' },
        { value: '7299', label: 'Miscellaneous Services - Services' },
        { value: '5311', label: 'Department Stores - Large retail' },
        { value: '5111', label: 'Stationery/Office Supplies - Office products' },
    ];

    const breadCrumbPath = [
        { name: `${language_data?.[authState.lang]?.common_home}`, url: `/`, active: '' },
        { name: `${language_data?.[authState.lang]?.common_profile}`, url: `/profile`, active: '' },
        { name: `Bank Details`, url: ``, active: 'not-allowed' }
    ];

    useEffect(() => {
        if (authState?.is_bank_added) {
            fetchBankDetails();
        }
    }, [authState?.is_bank_added]);

    const fetchBankDetails = async () => {
    setLoading(true);
    try {
        const apiRes = await henceforthApi.Bank.get();

        const bankData = apiRes?.data?.data?.[0]; // ✅ correct extraction

        if (bankData) {
            setState((prev: any) => ({
                ...prev,
                account_holder_name: bankData.account_holder_name || '',
                account_number:  '',
                routing_number: bankData.routing_number || '',
                bank_name: bankData.bank_name || '',
                business_website: bankData.business_website || '',
                industry: bankData.industry || '',
                ssn_last4_number: bankData.account_number_last4 || '',
                date_of_birth: bankData.date_of_birth || '',
                document_file_key: bankData.document_file_key || '',
                language: 'ENGLISH',
            }));
        }
    } catch (err) {
        handleError(err, 'active', authDispatch);
    } finally {
        setLoading(false);
    }
};

    const handleChange = (e: any) => {
        const { name, value } = e.target;
        setState({ ...state, [name]: value });
        setErrors({ ...errors, [name]: '' });
    };

    const handleFile = (e: any) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
            setErrors({ ...errors, document_file_key: '' });
        }
    };

    const validate = () => {
        const newErrors: any = {};
        if (!state.account_holder_name) newErrors.account_holder_name = 'Account holder name is required';
        if (!state.bank_name) newErrors.bank_name = 'Bank name is required';
        if (!state.account_number && !authState?.is_bank_added) newErrors.account_number = 'Account number is required';
        if (!state.routing_number) newErrors.routing_number = 'Routing number is required';
        if (!state.business_website) newErrors.business_website = 'Business website is required';
        if (!state.industry) newErrors.industry = 'Industry is required';
        if (!state.ssn_last4_number) newErrors.ssn_last4_number = 'SSN last 4 digits are required';
        if (!state.date_of_birth) newErrors.date_of_birth = 'Date of birth is required';
        if (!state.document_file_key && !selectedFile && !authState?.is_bank_added) newErrors.document_file_key = 'Document is required';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setLoading(true);
        try {
            let documentKey = state.document_file_key;
            if (selectedFile) {
                const uploadRes = await henceforthApi.Common.do_spaces_file_upload('file', selectedFile);
                documentKey = uploadRes.data.file_name;
            }

            const payload = {
                account_holder_name: state.account_holder_name,
                account_number: state.account_number,
                routing_number: state.routing_number,
                bank_name: state.bank_name,
                business_website: state.business_website,
                industry: state.industry,
                ssn_last4_number: state.ssn_last4_number,
                date_of_birth: state.date_of_birth,
                document_file_key: documentKey,
                language: state.language
            };

            if (authState?.is_bank_added) {
                await henceforthApi.Bank.edit(payload);
                toastMessage('Bank details updated successfully');
            } else {
                await henceforthApi.Bank.add(payload);
                toastMessage('Bank details added successfully');
            }

            const profileRes = await henceforthApi.Auth.getProfile();
            authAction.initialiseProfile(profileRes.data)(authDispatch);

            navigate('/profile');
        } catch (err) {
            handleError(err, 'active', authDispatch);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Fragment>
            <BreadCrumb pathNameDeclare={breadCrumbPath} />
            <div className="page-spacing">
                <section className="bank-details-section">
                    <div className="container-fluid">
                        <div className="row justify-content-center">
                            <div className="col-md-8">
                                <div className="common-card">
                                    <div className="common-card-title">
                                        <h5>Bank Details</h5>
                                    </div>
                                    <div className="common-card-content">
                                        <div className="row">
                                            <div className="col-md-6">
                                                <Inputs.Input label="Account Holder Name" name="account_holder_name" value={state.account_holder_name} handleChange={handleChange} error={errors.account_holder_name} placeholder="Enter Account Holder Name" />
                                            </div>
                                            <div className="col-md-6">
                                                <Inputs.Input label="Bank Name" name="bank_name" value={state.bank_name} handleChange={handleChange} error={errors.bank_name} placeholder="Enter Bank Name" />
                                            </div>
                                            <div className="col-md-6">
                                                <Inputs.Input label="Account Number" name="account_number" value={state.account_number} handleChange={handleChange} error={errors.account_number} placeholder="Enter Account Number" />
                                            </div>
                                            <div className="col-md-6">
                                                <Inputs.Input label="Routing Number" name="routing_number" value={state.routing_number} handleChange={handleChange} error={errors.routing_number} placeholder="Enter Routing Number" />
                                            </div>
                                            <div className="col-md-6">
                                                <Inputs.Input label="Business Website" name="business_website" value={state.business_website} handleChange={handleChange} error={errors.business_website} placeholder="https://www.example.com" />
                                            </div>
                                            <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label className="fw-bold mb-1">Industry</label>
                                                    <select 
                                                        className={`form-select ${errors.industry ? 'is-invalid' : ''}`} 
                                                        name="industry" 
                                                        value={state.industry} 
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select Industry</option>
                                                        {industryOptions.map((option) => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors.industry && <div className="text-danger small">{errors.industry}</div>}
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <Inputs.Input label="SSN Last 4 Digits" name="ssn_last4_number" value={state.ssn_last4_number} handleChange={handleChange} error={errors.ssn_last4_number} placeholder="Enter SSN Last 4 Digits" />
                                            </div>
                                            <div className="col-md-6">
                                                <Inputs.Input type="date" label="Date of Birth" name="date_of_birth" value={state.date_of_birth} handleChange={handleChange} error={errors.date_of_birth} placeholder="Select date" />
                                            </div>
                                            {/* Phone input commented out */}
                                            {/* <div className="col-md-6">
                                                <div className="form-group mb-3">
                                                    <label className="fw-bold mb-1">Phone</label>
                                                    <div className="d-flex gap-2">
                                                        <select className="form-select w-25" name="country_code" value={state.country_code} onChange={handleChange}>
                                                            <option value="91">+91</option>
                                                            <option value="1">+1</option>
                                                        </select>
                                                        <input type="text" className={`form-control ${errors.phone_number ? 'is-invalid' : ''}`} name="phone_number" value={state.phone_number} onChange={handleChange} placeholder="Enter Phone Number" />
                                                    </div>
                                                    {errors.phone_number && <div className="text-danger small">{errors.phone_number}</div>}
                                                </div>
                                            </div> */}
                                            {/* Address mapping commented out */}
                                            {/* <div className="col-md-12">
                                                <h6 className="fw-bold mt-3 mb-3">Address</h6>
                                                <Inputs.Input name="address_line" value={state.address_line} handleChange={handleChange} error={errors.address_line} placeholder="Address Line" />
                                                <div className="row mt-2">
                                                    <div className="col-md-4">
                                                        <Inputs.Input name="city" value={state.city} handleChange={handleChange} error={errors.city} placeholder="City" />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <Inputs.Input name="state" value={state.state} handleChange={handleChange} error={errors.state} placeholder="State" />
                                                    </div>
                                                    <div className="col-md-4">
                                                        <Inputs.Input name="postal_code" value={state.postal_code} handleChange={handleChange} error={errors.postal_code} placeholder="Postal Code" />
                                                    </div>
                                                </div>
                                            </div> */}
                                            <div className="col-md-12 mt-4">
                                                <div className="upload-box text-center border p-4 rounded bg-light">
                                                    <input type="file" id="bank_image" hidden onChange={handleFile} accept="image/*" />
                                                    <label htmlFor="bank_image" className="cursor-pointer">
                                                        <div className="mb-2">
                                                            <i className="fa fa-cloud-upload fa-3x text-muted"></i>
                                                        </div>
                                                        <h6 className="fw-bold">Upload Document</h6>
                                                        <p className="text-muted small">Drag and drop an image here, or click to browse</p>
                                                    </label>
                                                    {(selectedFile || state.document_file_key) && (
                                                        <div className="mt-3">
                                                            {selectedFile ? (
                                                                <img src={URL.createObjectURL(selectedFile)} alt="Preview" className="img-thumbnail" style={{ maxHeight: '150px' }} />
                                                            ) : (
                                                                <SignedImage 
                                                                    src={state.document_file_key || ''} 
                                                                    alt="Document" 
                                                                    className="img-thumbnail" 
                                                                    style={{ maxHeight: '150px' }}
                                                                    size="original"
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                    {errors.document_file_key && <div className="text-danger small mt-2">{errors.document_file_key}</div>}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <SaveCancelButton loading={loading} onClick={handleSubmit} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </Fragment>
    );
};

export default BankDetails;
